import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getUiLang } from '../utils/lang';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { User, Package, LogOut, Calendar, CreditCard } from 'lucide-react';
import LoyaltyWidget from '../components/LoyaltyWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Account = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, loading: authLoading, logout, getAuthHeader } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAuthenticated) {
      fetchOrders();
    }
  }, [isAuthenticated]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/auth/orders`, {
        headers: getAuthHeader()
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTranslation = (obj, field) => {
    return obj?.translations?.[getUiLang(i18n)]?.[field] || obj?.translations?.['sk']?.[field] || '';
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'processing': 'bg-yellow-100 text-yellow-800',
      'shipped': 'bg-purple-100 text-purple-800',
      'delivered': 'bg-green-100 text-green-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusText = (status) => {
    const texts = {
      'new': 'Nová',
      'processing': 'Spracováva sa',
      'shipped': 'Odoslaná',
      'delivered': 'Doručená',
      'cancelled': 'Zrušená'
    };
    return texts[status] || status;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="account-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-yellow-400 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2" data-testid="account-title">{t('account.title')}</h1>
          <p className="text-lg text-white/90">Vitajte späť, {user?.name}!</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center space-x-4 mb-6 pb-6 border-b border-gray-200">
                <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                  <User className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold">{user?.name}</h2>
                  <p className="text-gray-600 text-sm">{user?.email}</p>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between py-3 px-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Package className="h-5 w-5 text-orange-500" />
                    <span className="font-semibold">Objednávky</span>
                  </div>
                  <span className="bg-orange-500 text-white px-3 py-1 rounded-full text-sm">
                    {orders.length}
                  </span>
                </div>

                <button
                  onClick={logout}
                  className="w-full flex items-center space-x-3 py-3 px-4 text-red-600 hover:bg-red-50 rounded-lg transition"
                  data-testid="logout-button"
                >
                  <LogOut className="h-5 w-5" />
                  <span className="font-semibold">{t('account.logout')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Orders Section */}
          <div className="lg:col-span-2">
            <LoyaltyWidget />
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold mb-6" data-testid="orders-title">
                História objednávok
              </h2>

              {loading ? (
                <div className="text-center py-12">
                  <div className="text-xl text-orange-500">{t('common.loading')}</div>
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-xl text-gray-600">Zatiaľ nemáte žiadne objednávky</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {orders.map((order) => (
                    <div
                      key={order.id}
                      className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition"
                      data-testid={`order-${order.id}`}
                    >
                      {/* Order Header */}
                      <div className="flex flex-wrap items-center justify-between mb-4 pb-4 border-b border-gray-200">
                        <div>
                          <div className="text-sm text-gray-600 mb-1">
                            Objednávka #{order.id.substring(0, 8)}
                          </div>
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4" />
                            <span>
                              {new Date(order.created_at).toLocaleDateString('sk-SK')}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(order.order_status)}`}>
                            {getStatusText(order.order_status)}
                          </div>
                        </div>
                      </div>

                      {/* Order Items */}
                      <div className="space-y-3 mb-4">
                        {order.items.map((item, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="font-semibold">{item.product_name}</div>
                              <div className="text-sm text-gray-600">Množstvo: {item.quantity}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">
                                {order.currency === 'EUR' ? item.price_eur : item.price_usd} {order.currency}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Order Total */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <CreditCard className="h-5 w-5" />
                          <span>{order.payment_method}</span>
                        </div>
                        <div className="text-xl font-bold text-orange-500">
                          {order.currency === 'EUR' ? order.total_eur : order.total_usd} {order.currency}
                        </div>
                      </div>

                      {/* Delivery Info */}
                      <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                        <div className="font-semibold mb-2">Doručenie:</div>
                        <div>{order.delivery_method === 'courier' ? 'Kuriérska služba' : 'Dopravná spoločnosť'}</div>
                        <div className="mt-2">
                          {order.delivery_address.street}, {order.delivery_address.city}, {order.delivery_address.zip}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
